import { useRef } from "react";

import { formatInteger } from "@/shared/utils/format";

import { useChartTip } from "./chart-kit";
import { EmptyState } from "./EmptyState";

export type DonutSegment = { key: string; label: string; value: number; tone: string };

const SIZE = 168;
const RADIUS = 76;
const THICKNESS = 24;

function arc(start: number, end: number): string {
  const outer = RADIUS;
  const inner = RADIUS - THICKNESS;
  const point = (radius: number, angle: number) => {
    const radians = (angle - 90) * (Math.PI / 180);
    return `${(SIZE / 2 + radius * Math.cos(radians)).toFixed(2)},${(SIZE / 2 + radius * Math.sin(radians)).toFixed(2)}`;
  };
  const large = end - start > 180 ? 1 : 0;
  return [
    `M${point(outer, start)}`,
    `A${outer},${outer} 0 ${large} 1 ${point(outer, end)}`,
    `L${point(inner, end)}`,
    `A${inner},${inner} 0 ${large} 0 ${point(inner, start)}`,
    "Z"
  ].join(" ");
}

/**
 * Rosca em SVG — a forma de "parte de um todo" do v1 (volume e
 * engajamento por sentimento, Share of Voice, menção no título). Em SVG
 * e não no `conic-gradient` do `SentimentDonut`, pra sair no download.
 * A legenda leva número e percentual: a fatia nunca depende só da cor.
 */
export function DonutChart({ segments, centerValue, centerLabel, formatValue = formatInteger, title }: {
  segments: DonutSegment[];
  centerValue?: string;
  centerLabel?: string;
  formatValue?: (value: number) => string;
  title: string;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const { bind, tip } = useChartTip(frame);
  const shown = segments.filter(segment => segment.value > 0);
  const total = shown.reduce((sum, segment) => sum + segment.value, 0);
  if (!total) return <EmptyState/>;
  let angle = 0;
  const share = (value: number) => {
    const percent = (value / total) * 100;
    return percent > 0 && percent < 1 ? "<1%" : `${Math.round(percent)}%`;
  };
  return (
    <div className="chart-frame chart-donut-layout" ref={frame}>
      <svg data-export width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label={title}>
        {shown.length === 1 ? (
          <circle cx={SIZE / 2} cy={SIZE / 2} r={RADIUS - THICKNESS / 2} className={`chart-ring chart-stroke--${shown[0].tone}`} strokeWidth={THICKNESS} fill="none"/>
        ) : shown.map(segment => {
          const start = angle;
          angle += (segment.value / total) * 360;
          // 1,2° de folga entre fatias: o "respiro" de 2px da superfície.
          return (
            <path
              key={segment.key}
              d={arc(start + 0.6, Math.max(start + 0.7, angle - 0.6))}
              className={`chart-tone--${segment.tone}`}
              tabIndex={0}
              aria-label={`${segment.label}: ${formatValue(segment.value)} (${share(segment.value)})`}
              {...bind({ title: segment.label, lines: [{ label: "Total", value: `${formatValue(segment.value)} · ${share(segment.value)}` }] })}
            />
          );
        })}
        {centerValue && <text x={SIZE / 2} y={SIZE / 2 - 4} className="chart-donut-value" textAnchor="middle">{centerValue}</text>}
        {centerLabel && <text x={SIZE / 2} y={SIZE / 2 + 16} className="chart-axis-text" textAnchor="middle">{centerLabel}</text>}
      </svg>
      {tip}
      <ul className="chart-donut-legend">
        {shown.map(segment => (
          <li key={segment.key}>
            <span className={`chart-legend-swatch chart-tone--${segment.tone}`} aria-hidden="true"/>
            <span className="chart-donut-legend-label">{segment.label}</span>
            <strong>{formatValue(segment.value)}</strong>
            <small>{share(segment.value)}</small>
          </li>
        ))}
      </ul>
    </div>
  );
}
